import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

/** One entry in a column's `conditionalFormat` array. Match logic in the
 *  cell renderer is case-insensitive and trims whitespace, so backend
 *  variants like " active " or "Active" still hit a `col_value: 'ACTIVE'`
 *  rule. `isSelect: false` excludes the rule from matching. */
export interface ConditionalFormat {
  col_value: string;
  isSelect?: boolean;
  /** Name of an SVG icon registered with `MatIconRegistry` (see IconService). */
  icon?: string;
  /** Any valid CSS colour (hex, rgb, named) applied to the rendered icon. */
  color?: string;
}

/** Params shape passed by `BasicPopupGridComponent.processColumnDefs` when it
 *  wires this renderer onto a column. */
export interface IconCellParams {
  conditionalFormat: ConditionalFormat[];
  /** When set, look up the matching rule against this field from the row
   *  (e.g. display `status_group` but match against `case_status_group`).
   *  Defaults to the column's own value. */
  compareField?: string;
}

@Component({
    imports: [CommonModule, MatIconModule],
    selector: 'app-basic-popup-grid-icon-cell',
    template: `
    <span class="bpg-icon-cell">
      <mat-icon
        *ngIf="match?.icon"
        class="bpg-icon-cell__icon"
        [svgIcon]="match!.icon!"
        [style.color]="match!.color || null">
      </mat-icon>
      <span class="bpg-icon-cell__value">{{ displayValue }}</span>
    </span>
  `,
    styles: [`
    /* Make :host fill the ag-cell so flex centering pins the icon vertically
       inside the row (without relying on the inline-baseline that mat-icon
       defaults to, which was clipping the SVG at the bottom). */
    :host {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      overflow: visible;
    }
    .bpg-icon-cell {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      line-height: 1.2;
      min-width: 0;
    }
    .bpg-icon-cell__icon {
      /* Bumped from 16 → 20px for visibility. mat-icon needs matching
         width/height/font-size + line-height:1 so the SVG sits flush in
         its box without baseline padding clipping the bottom. */
      width: 20px;
      height: 20px;
      font-size: 20px;
      line-height: 1;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      overflow: visible;
    }
    /* Force the inner SVG to fill mat-icon's box. Without this, some
       registered icons keep their authored viewBox dimensions and render
       smaller (or off-center) inside the resized container. */
    .bpg-icon-cell__icon ::ng-deep svg {
      width: 100%;
      height: 100%;
      display: block;
    }
    .bpg-icon-cell__value { white-space: normal; }
  `]
})
export class BasicPopupGridIconCellComponent implements ICellRendererAngularComp {
  match: ConditionalFormat | undefined;
  displayValue: string = '';

  agInit(params: ICellRendererParams & IconCellParams): void {
    this.update(params);
  }

  refresh(params: ICellRendererParams & IconCellParams): boolean {
    this.update(params);
    return true;
  }

  private update(params: ICellRendererParams & IconCellParams): void {
    const rules: ConditionalFormat[] = params.conditionalFormat || [];
    const rawCompare = params.compareField
      ? (params.data || {})[params.compareField]
      : params.value;
    const key = this.normalise(rawCompare);
    this.match = key
      ? rules.find(r => r.isSelect !== false && this.normalise(r.col_value) === key)
      : undefined;
    this.displayValue = params.valueFormatted != null
      ? params.valueFormatted
      : (params.value == null ? '' : String(params.value));
  }

  private normalise(v: unknown): string {
    if (v === null || v === undefined) return '';
    return String(v).trim().toUpperCase();
  }
}
